/**
 * Pure helpers behind the "Run training" button (TRA-1173), kept out of
 * the components so they are testable without a DOM.
 */

import { z } from 'zod'

import type {
  TrainingLaunchRequest,
  GrpoPresetChanges,
  GrpoRecipeValues,
  SftPresetChanges,
  TrainingContextLengthOption,
  TrainingEpochsOption,
  TrainingGrpoDatasetOption,
  TrainingGrpoValidationOption,
  TrainingLaunchTargetView,
  TrainingLaunchView,
  TrainingLearningRateOption,
  TrainingLoraOption,
  TrainingLrScheduleOption,
  TrainingMultiNodeOption,
  TrainingRunDocument,
  TrainingSealedCheckpointOption,
  TrainingSetFit,
  TrainingSetOption,
  TrainingSetOptionsResponse,
  TrainingValidationPassesOption,
  TrainingWarmupFractionOption,
} from '@/api/generated/model'

/**
 * What the dialog trains on: an SFT set, keeper (TRA-1184) or uploaded
 * (TRA-1343), or a GRPO registry dataset (TRA-1377).
 */
export type LaunchSource = 'set' | 'grpo'

/** The pickers' data; undefined while its query has not answered yet. */
export interface LaunchSources {
  sets: TrainingSetOption[] | undefined
  grpo: TrainingGrpoDatasetOption[] | undefined
}

/** A grpo request's step count: the api's bounds, and the default it applies. */
export const GRPO_STEPS = { min: 2, max: 1000, default: 5 } as const

/**
 * Why the button is disabled, or null when a launch can go ahead. A demo
 * team's run is simulated and trains on nothing, so no empty picker blocks it.
 */
export function launchBlockedReason(
  launches: TrainingLaunchView[],
  sources: LaunchSources,
  demo: boolean,
): string | null {
  if (launches.some((launch) => launch.status === 'running')) {
    return 'A run is in progress. One run at a time.'
  }
  // Any source still loading may yet offer something, so only fully
  // answered sources can block.
  if (demo || sources.sets === undefined || sources.grpo === undefined) {
    return null
  }
  if (sources.sets.length === 0 && sources.grpo.length === 0) {
    return 'No data is published and no GRPO dataset is registered yet.'
  }
  return null
}

/**
 * Whether the dialog has what it opens with. A demo team's run trains on
 * nothing, so it waits for no source; for any other team one source that
 * answered is enough, since the other failing still leaves it to launch from.
 */
export function launchSourcesAnswered(sources: LaunchSources, demo: boolean): boolean {
  return demo || sources.sets !== undefined || sources.grpo !== undefined
}

/** The picker to open on: data when some is published, else GRPO datasets. */
export function defaultSource(sources: LaunchSources): LaunchSource {
  if ((sources.sets ?? []).length > 0) return 'set'
  return (sources.grpo ?? []).length > 0 ? 'grpo' : 'set'
}

/**
 * The target a launch runs on (TRA-1685): the one picked, or the stack's
 * first (its default) until one is. Undefined while the stack lists none.
 */
export function pickedTarget(
  targets: TrainingLaunchTargetView[],
  name: string,
): TrainingLaunchTargetView | undefined {
  return targets.find((target) => target.name === name) ?? targets[0]
}

/**
 * The description's first sentence: what node the run starts. A SkyPilot
 * target rents a cloud VM; a rayjob target takes one 8-GPU node from its
 * cluster's queue. Before the targets load, the stack's first is a
 * SkyPilot one on every hosted stack.
 */
export function nodeSentence(target: TrainingLaunchTargetView | undefined): string {
  if (target?.backend === 'rayjob') {
    return `This starts one 8-GPU node on ${target.label}.`
  }
  return 'This starts one 8xH200 or 8xH100 node, spot when available and on-demand otherwise.'
}

/** Why a launch from `source` cannot run on `target`, or null when it can. */
export function targetRefusal(
  target: TrainingLaunchTargetView | undefined,
  source: LaunchSource,
): string | null {
  return source === 'grpo' && target !== undefined && !target.grpo
    ? `${target.label} does not train GRPO runs.`
    : null
}

/** The line under a GRPO dataset's name: its split, and the outside models it calls. */
export function grpoDatasetSummary(option: TrainingGrpoDatasetOption): string {
  const split = `${option.training_tasks.toLocaleString()} training · ${option.validation_tasks.toLocaleString()} validation`
  return withRoutes(split, option.outside_model_routes)
}

/** The line beside a validation dataset's name: its tasks, and the outside models it calls. */
export function grpoValidationSummary(option: TrainingGrpoValidationOption): string {
  return withRoutes(`${option.validation_tasks.toLocaleString()} validation`, option.outside_model_routes)
}

/** The ticked ids the registry no longer lists, in the order they were ticked. */
export function missingValidationIds(options: TrainingGrpoValidationOption[], selected: string[]): string[] {
  return selected.filter((id) => !options.some((option) => option.registry_id === id))
}

/** The selection after one tick or clear, in the order the field lists the datasets. */
export function validationSelection(
  listed: string[],
  selected: string[],
  id: string,
  checked: boolean,
): string[] {
  return listed.filter((name) => (name === id ? checked : selected.includes(name)))
}

function withRoutes(split: string, routes: string[]): string {
  return routes.length === 0 ? split : `${split} · calls ${routes.join(', ')}`
}

/** A step count the api accepts, or null. */
export function grpoSteps(text: string): number | null {
  const steps = Number(text)
  return Number.isInteger(steps) && steps >= GRPO_STEPS.min && steps <= GRPO_STEPS.max
    ? steps
    : null
}

/**
 * What a launch row trained on, or for a serve the step and run it deploys.
 * A `p0` row is a one-task run from before TRA-1477: nothing starts one now,
 * and it reads as its base test and variant.
 */
export function launchLabel(
  launch: Pick<
    TrainingLaunchView,
    | 'kind'
    | 'base_stem'
    | 'variant'
    | 'set_id'
    | 'grpo_dataset'
    | 'max_steps'
    | 'serves_run_id'
    | 'serves_step'
  >,
): string {
  if (launch.kind === 'sft-set') return `data ${launch.set_id ?? ''}`
  if (launch.kind === 'grpo') {
    const steps = typeof launch.max_steps === 'number' ? `, ${String(launch.max_steps)} steps` : ''
    return `GRPO ${launch.grpo_dataset ?? ''}${steps}`
  }
  if (launch.kind === 'demo') return 'Demo run'
  if (launch.kind === 'serve') return `Serve ${launch.serves_step ?? ''} of ${launch.serves_run_id ?? ''}`
  return `${launch.base_stem ?? ''} ${launch.variant ?? ''}`.trim()
}

/** How a preset field a launch can change reads (TRA-1345, TRA-1564), in the order a row lists them. */
const PRESET_FIELD_LABEL: Record<keyof SftPresetChanges, string> = {
  epochs: 'epochs',
  learning_rate: 'learning rate',
  sequence_length: 'sequence length',
  validation_passes: 'validation passes',
  lora_rank: 'LoRA rank',
  lora_alpha: 'LoRA alpha',
  lr_schedule: 'schedule',
  warmup_fraction: 'warmup',
  sealed_checkpoint: 'sealed checkpoint',
}

function isPresetField(field: string): field is keyof SftPresetChanges {
  return field in PRESET_FIELD_LABEL
}

/** An unknown field shows as the api sent it. */
export function presetFieldLabel(field: string): string {
  return isPresetField(field) ? PRESET_FIELD_LABEL[field] : field
}

/** A preset value as people write it: a learning rate reads 2e-5, where String() gives 0.00002; a switch reads on or off; a name reads as it is. */
export function presetValue(value: number | boolean | string): string {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'on' : 'off'
  return value !== 0 && Math.abs(value) < 1e-3 ? value.toExponential() : String(value)
}

/** A launch row's changes to its derived preset, e.g. "epochs 3, learning rate 2e-5"; null when it kept the preset. */
export function presetChangesLine(changes: SftPresetChanges | null): string | null {
  if (changes === null) return null
  const fields = Object.keys(PRESET_FIELD_LABEL).filter(isPresetField)
  const parts = fields.flatMap((field) => {
    const value = changes[field]
    return value === undefined || value === null ? [] : [`${presetFieldLabel(field)} ${presetValue(value)}`]
  })
  return parts.length > 0 ? parts.join(', ') : null
}

/** The recipe switches a grpo launch can change: on, off, or the preset's own (schemas/training_run.py GrpoPresetChanges). */
export type GrpoFlagKey = 'validation_at_start' | 'validation_at_end' | 'thinking'
export type GrpoNumberKey = Exclude<keyof GrpoPresetChanges, GrpoFlagKey>

export interface GrpoChangeFlag {
  key: GrpoFlagKey
  label: string
}

export const GRPO_CHANGE_FLAGS: GrpoChangeFlag[] = [
  { key: 'validation_at_start', label: 'Validation at start' },
  { key: 'validation_at_end', label: 'Validation at end' },
  { key: 'thinking', label: 'Thinking' },
]

/** A recipe value a grpo launch can change, with the api's bounds (schemas/training_run.py GrpoPresetChanges). */
export interface GrpoChangeField {
  key: GrpoNumberKey
  label: string
  min: number
  max: number
  integer: boolean
  /** The value may not equal min (a rate or temperature of 0). */
  aboveMin: boolean
}

export const GRPO_CHANGE_FIELDS: GrpoChangeField[] = [
  { key: 'learning_rate', label: 'Learning rate', min: 0, max: 1e-3, integer: false, aboveMin: true },
  { key: 'prompts_per_step', label: 'Prompts per step', min: 1, max: 64, integer: true, aboveMin: false },
  { key: 'rollouts_per_prompt', label: 'Rollouts per prompt', min: 2, max: 64, integer: true, aboveMin: false },
  { key: 'rollout_concurrency', label: 'Rollout concurrency', min: 1, max: 256, integer: true, aboveMin: false },
  { key: 'temperature', label: 'Temperature', min: 0, max: 2, integer: false, aboveMin: true },
  { key: 'max_new_tokens', label: 'Max new tokens', min: 256, max: 131072, integer: true, aboveMin: false },
  { key: 'kl_penalty', label: 'KL penalty', min: 0, max: 1, integer: false, aboveMin: false },
  { key: 'validation_period', label: 'Validation period', min: 1, max: 1000, integer: true, aboveMin: false },
  { key: 'validation_repeats', label: 'Validation repeats', min: 1, max: 32, integer: true, aboveMin: false },
  { key: 'checkpoint_save_period', label: 'Checkpoint period', min: 1, max: 1000, integer: true, aboveMin: false },
]

export type GrpoChangeTexts = Partial<Record<keyof GrpoPresetChanges, string>>

/** The fields' text as a request's grpo_changes: null when every field is empty, and the fields the api would refuse. */
export function grpoChanges(texts: GrpoChangeTexts): {
  changes: GrpoPresetChanges | null
  invalid: (keyof GrpoPresetChanges)[]
} {
  const changes: GrpoPresetChanges = {}
  const invalid: (keyof GrpoPresetChanges)[] = []
  for (const field of GRPO_CHANGE_FIELDS) {
    const text = (texts[field.key] ?? '').trim()
    if (text === '') continue
    const value = Number(text)
    const inRange =
      Number.isFinite(value) &&
      (field.aboveMin ? value > field.min : value >= field.min) &&
      value <= field.max &&
      (!field.integer || Number.isInteger(value))
    if (inRange) changes[field.key] = value
    else invalid.push(field.key)
  }
  for (const flag of GRPO_CHANGE_FLAGS) {
    const text = (texts[flag.key] ?? '').trim()
    if (text === '') continue
    if (text === 'on' || text === 'off') changes[flag.key] = text === 'on'
    else invalid.push(flag.key)
  }
  return { changes: Object.keys(changes).length > 0 ? changes : null, invalid }
}

/**
 * A grpo launch's recipe changes as stored, e.g. "learning rate 1e-5, prompts
 * per step 2"; null when it kept the preset. A field this page no longer
 * offers shows under its stored name.
 */
export function grpoChangesLine(changes: Readonly<Record<string, number | boolean>> | null): string | null {
  if (changes === null) return null
  const offeredFields: { key: string; label: string }[] = [...GRPO_CHANGE_FIELDS, ...GRPO_CHANGE_FLAGS]
  const known = offeredFields.flatMap((field) => {
    const value = changes[field.key]
    return value === undefined ? [] : [`${field.label.toLowerCase()} ${presetValue(value)}`]
  })
  const offered = new Set<string>(offeredFields.map((field) => field.key))
  const retired = Object.entries(changes)
    .filter(([key]) => !offered.has(key))
    .map(([key, value]) => `${key} ${presetValue(value)}`)
  const parts = [...known, ...retired]
  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * What the dialog opens with to continue a grpo run from its saved checkpoint
 * (TRA-1430): the run's own dataset, validation datasets, preset and recipe
 * changes, the checkpoint as the starting checkpoint, and its total steps,
 * which the continued run counts on to from the checkpoint's step.
 */
export interface GrpoContinuation {
  datasetId: string
  validationIds: string[]
  checkpoint: string
  /** The optimizer step the checkpoint holds. */
  step: number
  preset: string
  stepsText: string
  changeTexts: GrpoChangeTexts
}

/**
 * Whether a launch has no run document yet: its node has not written
 * run.json to the bucket, so the page shows its row and nothing above it.
 * A multi-node run's node 0 writes the document from its first minute
 * (TRA-1564), as a RayJob's node does once it starts.
 */
export function launchWithoutMetrics(
  launches: TrainingLaunchView[],
  runs: TrainingRunDocument[],
): boolean {
  return launches.some((launch) => !runs.some((run) => run.run_id === launch.run_id))
}

/**
 * Whether a grpo launch made the run. Only those have validation and
 * held-out scores: the run document carries no kind, and a demo run has no
 * task like a grpo run (Devin on #1090).
 */
export function isGrpoRun(run: TrainingRunDocument, launches: TrainingLaunchView[]): boolean {
  return launches.some((launch) => launch.run_id === run.run_id && launch.kind === 'grpo')
}

/**
 * The step staff may serve from a grpo run whose own export failed: its best
 * artifact, when the run's own grpo launch and its run.json both failed, it
 * has no deployment, and no launch holds the stack's one slot. Null for every
 * other run. The api checks the same before a serve starts
 * (training_launch.serve_refusal) and decides.
 */
export function servableStep(run: TrainingRunDocument, launches: TrainingLaunchView[]): string | null {
  const own = launches.find((launch) => launch.run_id === run.run_id)
  if (own === undefined || own.kind !== 'grpo' || own.status !== 'failed') return null
  if (run.status !== 'failed' || run.promoted_to !== null) return null
  if (launches.some((launch) => launch.status === 'running')) return null
  return run.best_artifact
}

/**
 * The continuation of a run that ended before its last step: a failed or
 * stopped grpo launch whose run has a saved checkpoint below its total. Null
 * for every other run, a run still going included.
 */
export function grpoContinuation(
  run: TrainingRunDocument,
  launches: TrainingLaunchView[],
): GrpoContinuation | null {
  const saved = run.resume_checkpoint
  const launch = launches.find((item) => item.run_id === run.run_id)
  if (saved == null || launch === undefined || launch.kind !== 'grpo') return null
  if (launch.status !== 'failed' && launch.status !== 'stopped') return null
  const dataset = launch.grpo_dataset ?? null
  const steps = launch.max_steps ?? null
  if (dataset === null || steps === null || steps <= saved.step) return null
  const changeTexts: GrpoChangeTexts = {}
  for (const field of [...GRPO_CHANGE_FIELDS, ...GRPO_CHANGE_FLAGS]) {
    const value = launch.grpo_changes?.[field.key]
    if (value !== undefined) changeTexts[field.key] = presetValue(value)
  }
  return {
    datasetId: dataset,
    validationIds: launch.grpo_validation_datasets ?? [],
    checkpoint: saved.path,
    step: saved.step,
    preset: launch.preset,
    stepsText: String(steps),
    changeTexts,
  }
}

/** What an empty recipe field shows: the preset's current value, when the trainer has a plain number for it. */
export function recipePlaceholder(current: GrpoRecipeValues | undefined, key: keyof GrpoPresetChanges): string {
  const value = current?.[key]
  return value === undefined || value === null ? 'Preset value' : presetValue(value)
}

/** The picker's line under a set's name: what it holds, then the date it was published. */
export function setSummary(option: TrainingSetOption, published: string): string {
  if (option.kind === 'upload') {
    const validation = option.validation_rows ?? 0
    return `SFT set upload · ${option.row_count.toLocaleString()} training · ${validation.toLocaleString()} validation · ${published}`
  }
  return `${option.policy ?? ''} · ${option.row_count.toLocaleString()} rows · ${published}`
}

/**
 * How long each packed sequence is and how many GPUs share it (TRA-1559):
 * "32768 tokens split over 8 GPUs". At the recipe's own length each GPU
 * trains a sequence of its own.
 */
export function sequenceSummary(tokens: number, gpus: number): string {
  return gpus === 1 ? `${String(tokens)} tokens on one GPU` : `${String(tokens)} tokens split over ${String(gpus)} GPUs`
}

/**
 * Where the length comes from, under the dialog's sequence line: the
 * set's longest row as estimated when it was published, or no estimate
 * for a set published before rows were measured. The node counts the rows
 * exactly and may train at a longer length.
 */
export function sequenceHelp(longestRowTokens: number | null | undefined): string {
  if (longestRowTokens === null || longestRowTokens === undefined) {
    return 'This data was published before its rows were measured, so the node picks the length when it counts them.'
  }
  return `The longest row holds about ${longestRowTokens.toLocaleString()} tokens. The node counts every row and may pick a longer length, which its log names.`
}

/**
 * The dialog's description of a set launch where the deployment turns
 * multi-node training on (TRA-1560): the nodes it starts and what they do,
 * with `plan` (sftPlan) saying what the Advanced section chose.
 */
export function multiNodeDescription(option: TrainingMultiNodeOption, plan: string): string {
  const nodes = `${String(option.node_count)} nodes of ${String(option.gpus_per_node)} GPUs`
  const outputs = option.uploads_outputs
    ? "Every node uploads its part of the checkpoint and its log when the run ends. The run's row names the folder."
    : 'This stack has no bucket for the outputs, so every node keeps its part of the checkpoint and its log on its own disk.'
  return `This starts ${nodes} from the cluster's training queue. They fine-tune ${option.base_model} with its Automodel recipe on the data you picked${plan}. ${outputs} Nothing deploys the adapter yet.`
}

export const LAUNCH_STATUS_LABEL: Record<TrainingLaunchView['status'], string> = {
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  stopped: 'Stopped',
}

/** "1h 7m" for a millisecond span. */
export function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  return `${hours.toString()}h ${minutes.toString()}m`
}


/**
 * What the dialog's Advanced section offers a set launch, from GET /sets:
 * each field's default and bounds, so the web hard-codes neither
 * (TRA-1345). The rate is null where the stack can change none, and
 * learning_rate_notice says why. The validation passes, LoRA, schedule
 * and sealed checkpoint are null where the base model trains on one node,
 * whose trainer publishes none of them (TRA-1564).
 */
export type SftAdvancedOptions = Pick<
  TrainingSetOptionsResponse,
  | 'epochs'
  | 'learning_rate'
  | 'learning_rate_notice'
  | 'sequence_length'
  | 'validation_passes'
  | 'lora'
  | 'lr_schedule'
  | 'sealed_checkpoint'
>

/** A whole number of epochs within the api's bounds, or null. */
export function sftEpochs(text: string, option: TrainingEpochsOption): number | null {
  return wholeNumber(text, option.minimum, option.maximum)
}

/** A whole number from `minimum` to `maximum`, or null; a cleared field is no number at all. */
function wholeNumber(text: string, minimum: number, maximum: number): number | null {
  if (text.trim() === '') return null
  const parsed = z.coerce.number().int().min(minimum).max(maximum).safeParse(text.trim())
  return parsed.success ? parsed.data : null
}

/** A learning rate within the base model's bounds, written as 5e-5 or 0.00005, or null. */
export function sftLearningRate(text: string, option: TrainingLearningRateOption): number | null {
  return boundedNumber(text, option.minimum, option.maximum)
}

/**
 * A finite number from `minimum` to `maximum`, or null. A cleared field
 * is no number at all: coercion reads it as 0, which a warmup's bounds
 * take (Devin on #1335).
 */
function boundedNumber(text: string, minimum: number, maximum: number): number | null {
  if (text.trim() === '') return null
  const parsed = z.coerce.number().finite().min(minimum).max(maximum).safeParse(text.trim())
  return parsed.success ? parsed.data : null
}

/** A rate as a plain decimal, 0.00005 for 5e-5, to show beside its exponent form. */
export function decimalValue(value: number): string {
  const plain = String(value)
  return plain.includes('e') ? value.toFixed(20).replace(/0+$/, '') : plain
}

/**
 * One Advanced field: the api's bounds, the default the picked set's
 * recipe trains at, the text shown, and the value it parses to within the
 * bounds (null when it does not).
 */
export interface SftAdvancedField<Option> {
  option: Option
  default: number
  text: string
  value: number | null
}

/** An Advanced field picked from a list: the choices, the default, the one picked. */
export interface SftChoiceField<Choice> {
  choices: Choice[]
  default: Choice
  value: Choice
}

export type SftLrSchedule = TrainingLrScheduleOption['default']
export type SftSealedCheckpoint = TrainingSealedCheckpointOption['default']

/**
 * The least sequence length a run trains at (TRA-1559): the lengths the
 * base model allows from the one the picked set's rows need, that one by
 * default, and the one picked.
 */
export interface SftLengthField {
  choices: TrainingContextLengthOption[]
  default: number
  value: number
}

/** Alpha's bounds at the rank the run trains at (the api's multiples of it). */
export interface SftAlphaBounds {
  minimum: number
  maximum: number
}

/**
 * The fields only a base model with an Automodel recipe offers (TRA-1564).
 * Alpha's default and bounds follow the rank picked. Where the picked set's
 * validation rows cannot fill one sequence per GPU, the run skips
 * validation: the passes and the seal stay at their defaults, and
 * `skipsValidation` says why they are off.
 */
export interface SftAutomodelFields {
  length: SftLengthField
  passes: SftAdvancedField<TrainingValidationPassesOption>
  rank: SftChoiceField<number>
  alpha: SftAdvancedField<SftAlphaBounds>
  schedule: SftChoiceField<SftLrSchedule>
  minLr: number
  warmup: SftAdvancedField<TrainingWarmupFractionOption>
  seal: SftChoiceField<SftSealedCheckpoint>
  checkpointSeconds: number
  skipsValidation: string | null
}

/**
 * The dialog's Advanced section for a set launch (TRA-1345): the epochs
 * and the learning rate, each with the default the picked set's recipe
 * trains at and the api's bounds, and on the Automodel path the fields
 * only it offers. A field shows what was typed, or its default while
 * untouched. The rate is null where the stack offers no change to it, and
 * `notice` says why.
 */
export interface SftAdvanced {
  epochs: SftAdvancedField<TrainingEpochsOption>
  rate: SftAdvancedField<TrainingLearningRateOption> | null
  notice: string | null
  automodel: SftAutomodelFields | null
}

/** What was typed or picked in each Advanced field; null while it is untouched. */
export interface SftAdvancedTyped {
  epochs: string | null
  rate: string | null
  length: number | null
  passes: string | null
  rank: number | null
  alpha: string | null
  schedule: SftLrSchedule | null
  warmup: string | null
  seal: SftSealedCheckpoint | null
}

/** Every Advanced field at its default: what the dialog opens with and a reset returns to. */
export const SFT_UNTOUCHED: SftAdvancedTyped = {
  epochs: null,
  rate: null,
  length: null,
  passes: null,
  rank: null,
  alpha: null,
  schedule: null,
  warmup: null,
  seal: null,
}

/** Why the passes and the seal are off for a set whose run skips validation. */
export const SKIPS_VALIDATION =
  "This set's validation rows fill fewer sequences than the run trains at once, so the run skips validation."

/**
 * What the Advanced fields read of a set's multi-node fit (TRA-1559): the
 * length its rows need and the lengths at which its validation rows fill
 * every group. Null where the run trains on one node.
 */
export type SftSetLengths = Pick<TrainingSetFit, 'sequence_length' | 'validating_lengths'>

export function sftAdvanced(
  options: SftAdvancedOptions,
  recipe: TrainingSetOption['recipe'],
  typed: SftAdvancedTyped,
  fit: SftSetLengths | null = null,
): SftAdvanced {
  const epochsText = typed.epochs ?? String(options.epochs.default)
  return {
    epochs: {
      option: options.epochs,
      default: options.epochs.default,
      text: epochsText,
      value: sftEpochs(epochsText, options.epochs),
    },
    rate: options.learning_rate === null ? null : rateField(options.learning_rate, recipe, typed.rate),
    notice: options.learning_rate_notice,
    automodel: automodelFields(options, typed, fit),
  }
}

function rateField(
  option: TrainingLearningRateOption,
  recipe: TrainingSetOption['recipe'],
  typed: string | null,
): SftAdvancedField<TrainingLearningRateOption> {
  // The trainer's default for the set's recipe, which the launch keeps
  // when the field is left at it.
  const recipeDefault = option.default[recipe]
  const text = typed ?? presetValue(recipeDefault)
  return { option, default: recipeDefault, text, value: sftLearningRate(text, option) }
}

function automodelFields(
  options: SftAdvancedOptions,
  typed: SftAdvancedTyped,
  fit: SftSetLengths | null,
): SftAutomodelFields | null {
  const { validation_passes: passes, lora, lr_schedule: schedule, sealed_checkpoint: seal, sequence_length: lengths } = options
  if (passes === null || lora === null || schedule === null || seal === null || lengths === null) return null
  const length = lengthField(lengths.lengths, fit?.sequence_length ?? lengths.default, typed.length)
  // A longer length leaves fewer groups to fill, so the set may validate
  // at the one picked though not at its own. A run that skips validation
  // keeps the passes and the seal it cannot use.
  const validates = fit === null || fit.validating_lengths.includes(length.value)
  const passesText = (validates ? typed.passes : null) ?? String(passes.default)
  return {
    length,
    passes: { option: passes, default: passes.default, text: passesText, value: wholeNumber(passesText, passes.minimum, passes.maximum) },
    ...loraFields(lora, typed),
    schedule: { choices: schedule.schedules, default: schedule.default, value: typed.schedule ?? schedule.default },
    minLr: schedule.min_lr,
    warmup: warmupField(schedule.warmup_fraction, typed.warmup),
    seal: { choices: seal.choices, default: seal.default, value: (validates ? typed.seal : null) ?? seal.default },
    checkpointSeconds: seal.checkpoint_seconds,
    skipsValidation: validates ? null : SKIPS_VALIDATION,
  }
}

/**
 * The lengths a launch of the picked set may choose: the one its rows need
 * and the longer ones. A length picked for another set that this one's
 * rows do not fit falls back to this set's own.
 */
function lengthField(
  lengths: TrainingContextLengthOption[],
  needed: number,
  typed: number | null,
): SftLengthField {
  const choices = lengths.filter((length) => length.tokens >= needed)
  const value = typed !== null && choices.some((choice) => choice.tokens === typed) ? typed : needed
  return { choices, default: needed, value }
}

/** The GPUs that share a sequence at the length picked. */
export function lengthGpus(field: SftLengthField): number {
  return field.choices.find((choice) => choice.tokens === field.value)?.gpus_per_sequence ?? 1
}

function loraFields(
  lora: TrainingLoraOption,
  typed: SftAdvancedTyped,
): Pick<SftAutomodelFields, 'rank' | 'alpha'> {
  const rank = typed.rank ?? lora.default_rank
  const bounds = { minimum: rank * lora.alpha_per_rank_minimum, maximum: rank * lora.alpha_per_rank_maximum }
  // Alpha follows the rank while untouched, as the api derives it.
  const alphaDefault = rank * lora.alpha_per_rank
  const text = typed.alpha ?? String(alphaDefault)
  return {
    rank: { choices: lora.ranks, default: lora.default_rank, value: rank },
    alpha: { option: bounds, default: alphaDefault, text, value: wholeNumber(text, bounds.minimum, bounds.maximum) },
  }
}

function warmupField(
  option: TrainingWarmupFractionOption,
  typed: string | null,
): SftAdvancedField<TrainingWarmupFractionOption> {
  const text = typed ?? String(option.default)
  return { option, default: option.default, text, value: boundedNumber(text, option.minimum, option.maximum) }
}

/** Every typed Advanced field: the ones a value goes into. */
function typedFields(advanced: SftAdvanced): SftAdvancedField<unknown>[] {
  const automodel = advanced.automodel
  return [
    advanced.epochs,
    ...(advanced.rate === null ? [] : [advanced.rate]),
    ...(automodel === null ? [] : [automodel.passes, automodel.alpha, automodel.warmup]),
  ]
}

/** Whether every Advanced field holds a value the api accepts. */
export function sftAdvancedValid(advanced: SftAdvanced): boolean {
  return typedFields(advanced).every((field) => field.value !== null)
}

/**
 * How many Advanced fields are off the recipe's default: a value other
 * than the default, or no value within the bounds at all, so a cleared
 * field still has a reset (Devin on #1323). Alpha counts only when it is
 * off the default of the rank picked.
 */
export function sftAdvancedChanged(advanced: SftAdvanced): number {
  const automodel = advanced.automodel
  const picked = automodel === null ? [] : [automodel.length, automodel.rank, automodel.schedule, automodel.seal]
  const typed = typedFields(advanced).filter((field) => field.value !== field.default)
  return typed.length + picked.filter((field) => field.value !== field.default).length
}

/** The disclosure's summary: the changed fields, or that one needs a value before a launch. */
export function sftAdvancedLabel(advanced: SftAdvanced): string {
  if (!sftAdvancedValid(advanced)) return 'Advanced (a value is outside its bounds)'
  const changed = sftAdvancedChanged(advanced)
  if (changed === 0) return 'Advanced'
  return `Advanced (${String(changed)} change${changed === 1 ? '' : 's'})`
}

/**
 * What the description says a set launch trains for: " for 2 epochs at
 * learning rate 5e-5". Empty while a field is invalid, and without the
 * rate where the stack offers no change to it.
 */
export function sftPlan(advanced: SftAdvanced): string {
  const epochs = advanced.epochs.value
  if (epochs === null || !sftAdvancedValid(advanced)) return ''
  const rate =
    advanced.rate === null || advanced.rate.value === null
      ? ''
      : ` at learning rate ${presetValue(advanced.rate.value)}`
  return ` for ${String(epochs)} epoch${epochs === 1 ? '' : 's'}${rate}`
}

/** A field's value under its name when it differs from the default, else nothing. */
function changed<Name extends keyof SftPresetChanges, Value extends SftPresetChanges[Name]>(
  name: Name,
  value: Value | null,
  fallback: Value,
): Partial<SftPresetChanges> {
  return value === null || value === fallback ? {} : { [name]: value }
}

/** The Automodel fields' part of preset_changes: each one off its default. */
function automodelChanges(automodel: SftAutomodelFields | null): Partial<SftPresetChanges> {
  if (automodel === null) return {}
  const { length, passes, rank, alpha, schedule, warmup, seal } = automodel
  return {
    ...changed('sequence_length', length.value, length.default),
    ...changed('validation_passes', passes.value, passes.default),
    ...changed('lora_rank', rank.value, rank.default),
    ...changed('lora_alpha', alpha.value, alpha.default),
    ...changed('lr_schedule', schedule.value, schedule.default),
    ...changed('warmup_fraction', warmup.value, warmup.default),
    ...changed('sealed_checkpoint', seal.value, seal.default),
  }
}

/**
 * The launch request for a set: the starting adapter when one is named,
 * and under preset_changes each Advanced field whose value differs from
 * the recipe's default. The api refuses a change to the value the derived
 * preset already has, so a field at its default is left out, and alpha
 * at the default of the rank picked follows that rank. Null while a field
 * is invalid.
 */
export function sftLaunchRequest(
  setId: string,
  checkpoint: string,
  advanced: SftAdvanced,
): TrainingLaunchRequest | null {
  if (!sftAdvancedValid(advanced)) return null
  const { epochs, rate } = advanced
  const changes: SftPresetChanges = {
    ...changed('epochs', epochs.value, epochs.default),
    ...(rate === null ? {} : changed('learning_rate', rate.value, rate.default)),
    ...automodelChanges(advanced.automodel),
  }
  return {
    set_id: setId,
    ...(checkpoint.trim() === '' ? {} : { initial_checkpoint: checkpoint.trim() }),
    ...(Object.keys(changes).length === 0 ? {} : { preset_changes: changes }),
  }
}

/** Where a run's artifact is deployed, by provider: run.json's promoted_to. */
export type TrainingPromotion = NonNullable<TrainingRunDocument['promoted_to']>

/** One line naming the deployment, by provider. */
export function deploymentLabel(promoted: TrainingPromotion): string {
  if (promoted.provider === 'baseten') {
    return `${promoted.provider} ${promoted.model_id} (${promoted.environment})`
  }
  return `${promoted.provider} ${promoted.served_model} (${promoted.deployment})`
}

/**
 * Whether staff may record an in-cluster deployment of the run's sealed
 * adapter (TRA-1600): the run's own sft-set launch and its run.json both
 * completed, the adapter is listed as `final`, and nothing is deployed yet.
 * The page offers it on an enterprise install alone; the api checks the
 * same before it writes (training_launch.deployment_refusal).
 */
export function recordableAdapter(run: TrainingRunDocument, launches: TrainingLaunchView[]): boolean {
  const own = launches.find((launch) => launch.run_id === run.run_id)
  if (own === undefined || own.kind !== 'sft-set' || own.status !== 'completed') return false
  if (run.status !== 'completed' || run.promoted_to !== null) return false
  return run.artifacts.some((artifact) => artifact.name === 'final' && artifact.kind === 'adapter')
}
